import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { patientProfileSchema, type PatientProfileInput } from "@dosa/validation";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FieldError,
  FieldLabel,
  Input,
  PageLoading,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@dosa/ui";
import { useAuth } from "../../hooks/useAuth";
import { getPatient, updatePatientProfile } from "../../lib/api-client";

export function PatientProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const patientId = user!.patientId!;
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);

  const { data: patient, isLoading } = useQuery({ queryKey: ["patient", patientId], queryFn: () => getPatient(patientId) });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PatientProfileInput>({ resolver: zodResolver(patientProfileSchema) });

  useEffect(() => {
    if (patient) {
      reset({
        firstName: patient.firstName,
        lastName: patient.lastName,
        firstNameAr: patient.firstNameAr,
        lastNameAr: patient.lastNameAr,
        dateOfBirth: patient.dateOfBirth,
        biologicalSex: patient.biologicalSex,
        city: patient.city,
        nationality: patient.nationality,
        heightCm: patient.heightCm,
        weightKg: patient.weightKg,
        emergencyContactName: patient.emergencyContactName,
        emergencyContactPhone: patient.emergencyContactPhone,
      });
    }
  }, [patient, reset]);

  const mutation = useMutation({
    mutationFn: (input: PatientProfileInput) => updatePatientProfile(patientId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient", patientId] });
      setSaved(true);
    },
  });

  if (isLoading) return <PageLoading />;

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="text-2xl font-semibold text-charcoal-900">{t("patient.profile.title")}</h1>
      {saved && <Alert variant="success">{t("patient.profile.saved")}</Alert>}

      <Card>
        <CardHeader>
          <CardTitle>{t("patient.profile.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-5" noValidate>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel htmlFor="firstName">{t("patient.profile.firstName")}</FieldLabel>
                <Input id="firstName" aria-invalid={!!errors.firstName} {...register("firstName")} />
                <FieldError>{errors.firstName?.message}</FieldError>
              </div>
              <div>
                <FieldLabel htmlFor="lastName">{t("patient.profile.lastName")}</FieldLabel>
                <Input id="lastName" aria-invalid={!!errors.lastName} {...register("lastName")} />
                <FieldError>{errors.lastName?.message}</FieldError>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel htmlFor="dateOfBirth">{t("patient.profile.dateOfBirth")}</FieldLabel>
                <Input id="dateOfBirth" type="date" aria-invalid={!!errors.dateOfBirth} {...register("dateOfBirth")} />
                <FieldError>{errors.dateOfBirth?.message}</FieldError>
              </div>
              <div>
                <FieldLabel htmlFor="biologicalSex">{t("patient.profile.biologicalSex")}</FieldLabel>
                <Controller
                  control={control}
                  name="biologicalSex"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      items={{ MALE: t("patient.profile.male"), FEMALE: t("patient.profile.female") }}
                    >
                      <SelectTrigger id="biologicalSex">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MALE">{t("patient.profile.male")}</SelectItem>
                        <SelectItem value="FEMALE">{t("patient.profile.female")}</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel htmlFor="city">{t("patient.profile.city")}</FieldLabel>
                <Input id="city" {...register("city")} />
              </div>
              <div>
                <FieldLabel htmlFor="nationality">{t("patient.profile.nationality")}</FieldLabel>
                <Input id="nationality" {...register("nationality")} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel htmlFor="heightCm">{t("patient.profile.height")}</FieldLabel>
                <Input id="heightCm" type="number" {...register("heightCm")} />
              </div>
              <div>
                <FieldLabel htmlFor="weightKg">{t("patient.profile.weight")}</FieldLabel>
                <Input id="weightKg" type="number" {...register("weightKg")} />
              </div>
            </div>
            <div>
              <FieldLabel htmlFor="emergencyContactName">{t("patient.profile.emergencyContact")}</FieldLabel>
              <div className="grid grid-cols-2 gap-4">
                <Input id="emergencyContactName" placeholder={t("patient.profile.firstName")} {...register("emergencyContactName")} />
                <Input placeholder="+966 5x xxx xxxx" {...register("emergencyContactPhone")} />
              </div>
            </div>
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.save")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
